import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  TrashIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

const ROLES = ['SUPER_ADMIN', 'MANAGING_ADMIN', 'ADMIN', 'DATA_ANALYST', 'VIEWER'];

function CreateUserModal({ isOpen, onClose, companies, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'ADMIN',
    companyId: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminApi.createUser(formData);
      toast.success('User created! Verification email sent.');
      onSuccess();
      onClose();
      setFormData({ email: '', firstName: '', lastName: '', role: 'ADMIN', companyId: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Create New User
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(p => ({ ...p, firstName: e.target.value }))}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(p => ({ ...p, lastName: e.target.value }))}
                  className="input"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                className="input"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}
                className="input"
              >
                {ROLES.map(role => (
                  <option key={role} value={role}>
                    {role.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Company (Optional for Super/Managing Admin)
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData(p => ({ ...p, companyId: e.target.value }))}
                className="input"
              >
                <option value="">No Company</option>
                {companies?.map(company => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary flex-1">
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function EditUserModal({ isOpen, onClose, user, companies, onSuccess, currentUserRole }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    role: '',
    companyId: ''
  });
  
  const ROLES = ['SUPER_ADMIN', 'MANAGING_ADMIN', 'ADMIN', 'DATA_ANALYST', 'VIEWER'];
  
  // Roles that don't require a company
  const noCompanyRoles = ['SUPER_ADMIN', 'MANAGING_ADMIN'];
  
  useEffect(() => {
    if (user) {
      setFormData({
        role: user.role || 'ADMIN',
        companyId: user.companyId?._id || user.companyId || ''
      });
    }
  }, [user]);
  
  // When role changes, clear company if it's a no-company role
  useEffect(() => {
    if (noCompanyRoles.includes(formData.role)) {
      setFormData(prev => ({ ...prev, companyId: '' }));
    }
  }, [formData.role]);
  
  const requiresCompany = !noCompanyRoles.includes(formData.role);
  
  // Filter available roles based on current user's role
  const availableRoles = currentUserRole === 'SUPER_ADMIN' 
    ? ROLES 
    : ROLES.filter(r => r !== 'SUPER_ADMIN'); // Managing Admin can't assign Super Admin
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate company is required for non-admin roles
    if (requiresCompany && !formData.companyId) {
      toast.error('Company is required for this role');
      return;
    }
    
    setLoading(true);
    
    try {
      await adminApi.updateUser(user._id, {
        role: formData.role,
        companyId: requiresCompany ? formData.companyId : null
      });
      toast.success('User updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) return null;
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100">
              Edit User
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* User Info Display */}
          <div className="mb-6 p-4 bg-surface-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-surface-700 flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-surface-400" />
              </div>
              <div>
                <p className="font-medium text-surface-100">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-surface-400">{user.email}</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}
                className="input"
              >
                {availableRoles.map(role => (
                  <option key={role} value={role}>
                    {role.replace('_', ' ')}
                  </option>
                ))}
              </select>
              {noCompanyRoles.includes(formData.role) && (
                <p className="text-xs text-surface-400 mt-1">
                  This role has platform-wide access and doesn't require a company.
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Company {requiresCompany && <span className="text-red-400">*</span>}
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData(p => ({ ...p, companyId: e.target.value }))}
                className="input"
                disabled={!requiresCompany}
                required={requiresCompany}
              >
                <option value="">
                  {requiresCompany ? 'Select a Company' : 'No Company (Platform-wide Access)'}
                </option>
                {companies?.map(company => (
                  <option key={company._id} value={company._id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {requiresCompany && !formData.companyId && (
                <p className="text-xs text-red-400 mt-1">
                  Company is required for this role.
                </p>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading || (requiresCompany && !formData.companyId)} 
                className="btn-primary flex-1"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function ActivationLinkModal({ isOpen, onClose, user }) {
  const [loading, setLoading] = useState(false);
  const [activationData, setActivationData] = useState(null);
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    if (isOpen && user) {
      loadActivationLink();
    }
  }, [isOpen, user]);
  
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);
  
  const loadActivationLink = async () => {
    setLoading(true);
    setActivationData(null);
    try {
      const response = await adminApi.getActivationLink(user._id);
      setActivationData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to get activation link');
      onClose();
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = () => {
    if (activationData?.activationLink) {
      navigator.clipboard.writeText(activationData.activationLink);
      setCopied(true);
      toast.success('Link copied to clipboard!');
    }
  };
  
  if (!user) return null;
  
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="card w-full max-w-2xl">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-lg font-semibold text-surface-100 flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-primary-400" />
              Activation Link
            </Dialog.Title>
            <button onClick={onClose} className="text-surface-400 hover:text-surface-100">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* User Info */}
          <div className="mb-6 p-4 bg-surface-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-surface-700 flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-surface-400" />
              </div>
              <div>
                <p className="font-medium text-surface-100">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-sm text-surface-400">{user.email}</p>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activationData ? (
            <div className="space-y-4">
              {/* Activation Link */}
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Activation Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={activationData.activationLink}
                    readOnly
                    className="input flex-1 font-mono text-sm"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="btn-primary flex items-center gap-2 whitespace-nowrap"
                  >
                    {copied ? (
                      <>
                        <ClipboardDocumentCheckIcon className="w-5 h-5" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="w-5 h-5" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Expiration Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-surface-800 rounded-lg">
                  <p className="text-xs text-surface-400 mb-1">Expires At</p>
                  <p className="text-sm font-medium text-surface-100">
                    {new Date(activationData.expiresAt).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-surface-800 rounded-lg">
                  <p className="text-xs text-surface-400 mb-1">Time Remaining</p>
                  <p className="text-sm font-medium text-surface-100">
                    {activationData.hoursRemaining} hours
                  </p>
                </div>
              </div>
              
              {/* Status Info */}
              {activationData.newTokenGenerated && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <p className="text-sm text-amber-400">
                    ⚠️ A new token was generated because the previous one expired.
                  </p>
                </div>
              )}
              
              {/* Instructions */}
              <div className="p-4 bg-surface-800 rounded-lg">
                <h4 className="text-sm font-medium text-surface-100 mb-2">Instructions</h4>
                <ol className="text-sm text-surface-300 space-y-1 list-decimal list-inside">
                  <li>Copy the activation link above</li>
                  <li>Send it to the user via your preferred communication method</li>
                  <li>The user has {activationData.hoursRemaining} hours to use this link</li>
                  <li>After clicking the link, they can set their password and activate their account</li>
                </ol>
              </div>
            </div>
          ) : null}
          
          <div className="flex gap-3 pt-6 mt-6 border-t border-surface-700">
            <button onClick={onClose} className="btn-secondary flex-1">
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function StatusBadge({ isActive, isEmailVerified }) {
  if (!isEmailVerified) {
    return (
      <span className="badge-warning flex items-center gap-1">
        <ClockIcon className="w-3 h-3" />
        Pending
      </span>
    );
  }
  if (isActive) {
    return (
      <span className="badge-success flex items-center gap-1">
        <CheckCircleIcon className="w-3 h-3" />
        Active
      </span>
    );
  }
  return (
    <span className="badge-danger flex items-center gap-1">
      <XCircleIcon className="w-3 h-3" />
      Inactive
    </span>
  );
}

function RoleBadge({ role }) {
  const colors = {
    SUPER_ADMIN: 'badge-danger',
    MANAGING_ADMIN: 'badge-warning',
    ADMIN: 'badge-primary',
    DATA_ANALYST: 'badge-info',
    VIEWER: 'badge-neutral'
  };
  
  return (
    <span className={colors[role] || 'badge-neutral'}>
      {role?.replace('_', ' ')}
    </span>
  );
}

export default function Users() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ search: '', role: '', status: '', companyId: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivationLinkModal, setShowActivationLinkModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activationLinkUser, setActivationLinkUser] = useState(null);
  
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  
  useEffect(() => {
    loadUsers();
    loadCompanies();
  }, [pagination.page, filters]);
  
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getUsers({
        page: pagination.page,
        limit: 20,
        ...filters
      });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const loadCompanies = async () => {
    try {
      const response = await adminApi.getCompanies({ limit: 100 });
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Failed to load companies');
    }
  };
  
  const toggleUserStatus = async (user) => {
    try {
      await adminApi.updateUser(user._id, { isActive: !user.isActive });
      toast.success(`User ${user.isActive ? 'deactivated' : 'activated'}`);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };
  
  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };
  
  const handleResendVerification = async (user) => {
    try {
      await adminApi.resendVerificationEmail(user._id);
      toast.success(`Verification email resent to ${user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to resend verification email');
    }
  };
  
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await adminApi.deleteUser(user._id);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };
  
  const handleGetActivationLink = (user) => {
    setActivationLinkUser(user);
    setShowActivationLinkModal(true);
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">User Management</h1>
          <p className="text-surface-400 mt-1">Manage all users across the platform</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <PlusIcon className="w-5 h-5" />
          Add User
        </button>
      </div>
      
      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              type="text"
              placeholder="Search users..."
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
              className="input pl-10"
            />
          </div>
          
          <select
            value={filters.role}
            onChange={(e) => setFilters(p => ({ ...p, role: e.target.value }))}
            className="input"
          >
            <option value="">All Roles</option>
            {ROLES.map(role => (
              <option key={role} value={role}>{role.replace('_', ' ')}</option>
            ))}
          </select>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
            className="input"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending Verification</option>
          </select>
          
          <select
            value={filters.companyId}
            onChange={(e) => setFilters(p => ({ ...p, companyId: e.target.value }))}
            className="input"
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company._id} value={company._id}>{company.name}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Users Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Company</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-surface-400">
                  No users found
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user._id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-surface-400" />
                      </div>
                      <div>
                        <p className="font-medium text-surface-100">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-surface-400 flex items-center gap-1">
                          <EnvelopeIcon className="w-3 h-3" />
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <RoleBadge role={user.role} />
                  </td>
                  <td className="text-surface-300">
                    {user.companyId?.name || <span className="text-surface-500">—</span>}
                  </td>
                  <td>
                    <StatusBadge isActive={user.isActive} isEmailVerified={user.isEmailVerified} />
                  </td>
                  <td className="text-surface-400 text-sm">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {isSuperAdmin && (
                        <button
                          onClick={() => handleEditUser(user)}
                          className="btn-ghost btn-sm text-primary-400 flex items-center gap-1"
                          title="Edit user"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                          Edit
                        </button>
                      )}
                      {!user.isEmailVerified && (
                        <>
                          <button
                            onClick={() => handleResendVerification(user)}
                            className="btn-ghost btn-sm text-amber-400 flex items-center gap-1"
                            title="Resend verification email"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                            Resend
                          </button>
                          <button
                            onClick={() => handleGetActivationLink(user)}
                            className="btn-ghost btn-sm text-blue-400 flex items-center gap-1"
                            title="Get activation link"
                          >
                            <LinkIcon className="w-4 h-4" />
                            Get Link
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleUserStatus(user)}
                        className={user.isActive ? 'btn-ghost btn-sm text-red-400' : 'btn-ghost btn-sm text-emerald-400'}
                      >
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      {user._id !== currentUser?._id && (
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="btn-ghost btn-sm text-red-500 flex items-center gap-1"
                          title="Delete user"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-surface-400 text-sm">
            Showing {users.length} of {pagination.total} users
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        companies={companies}
        onSuccess={loadUsers}
      />
      
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingUser(null);
        }}
        user={editingUser}
        companies={companies}
        onSuccess={loadUsers}
        currentUserRole={currentUser?.role}
      />
      
      <ActivationLinkModal
        isOpen={showActivationLinkModal}
        onClose={() => {
          setShowActivationLinkModal(false);
          setActivationLinkUser(null);
        }}
        user={activationLinkUser}
      />
    </div>
  );
}

